import {
    ClientHookInfiniteQueryOptions,
    useVahakInfiniteQuery,
    useVahakMutationQuery
} from "@vahak/core/dist/hooks/useVahakQueryClient";

import useVahakHttpClient from "@vahak/core/dist/hooks/useVahakHttpClient";
import API from "@vahak/core/dist/_services/const";
import { QueryNames } from "@vahak/core/dist/_services/hooks/queryNames";
import {
    SubscriptionPlan,
    TicketType
} from "../components/admin/ticket-system-helper/constants";
import { TicketData } from "../components/admin/ticket-system-helper/types";
import { ApiErrorResponseType } from "@vahak/core/dist/custom-types";
import { getUniqueArray } from "@vahak/core/dist/methods/getUniqueArray";

interface GetTicketsParam {
    ticket_type?: number;
    ticketTypesList?: number[];
    status?: number;
    status_ids?: number[];
    cityId?: number;
    cityIds?: number[];
    stateIds?: number[];
    phone_number?: string;
    subscription_plan_id?: SubscriptionPlan;
    limit?: number;
    startDate?: number;
    endDate?: number;
    referenceId?: number[];
    referenceType?: number;
    source?: number;
    serviceType?: SubscriptionPlan;
    companyId?: number;
    ticketAssignedTo?: number;
    lorryTypeId?: number;
    lorryTypeIds?: number[];
    description?: string;
    dispositionId?: number;
}

export interface GetTicketsResponse {
    data: {
        total_pages: number;
        total_records: number;
        page_number: number;
        tickets_list: TicketData[];
    };
}

const getAdditionalPreloads = (ticketTypes: number[]) => {
    const basic = ["company_details", "updated_by_details"];
    if (!ticketTypes.length) {
        return basic;
    }
    return getUniqueArray(
        ticketTypes.flatMap((ticketType) => {
            switch (ticketType) {
                case TicketType.REFUND_REQUEST: {
                    return ["requested_by_details", "requested_to_details", "updated_by_details"];
                }
                case TicketType.VAS_LEADS_GPS:
                case TicketType.VAS_LEADS_CARGO:
                case TicketType.COMPANY_REGISTRATION:
                case TicketType.ADS_LEADS:
                case TicketType.TSO_LEAD_FORM: {
                    return [...basic, "agent_details"];
                }
                case TicketType.BID_CANCELLATION: {
                    return ["cancel_tap_n_go", "updated_by_details"];
                }
                case TicketType.IN_TRANSIT_CALLBACK:
                case TicketType.CALL_ME_BACK: {
                    return [...basic, "assigned_to_details"];
                }
                default: {
                    return basic;
                }
            }
        })
    );
};

const mapPayload = (params: GetTicketsParam) => {
    const { ticket_type, status, phone_number, subscription_plan_id, cityId, limit, endDate, startDate, referenceId, ticketTypesList } =
        params;
    const ticketTypes = getUniqueArray(
        [ticket_type, ...(ticketTypesList ?? [])].filter((type): type is number => Number.isInteger(type))
    );
    return {
        ticket_type,
        status,
        limit,
        subscription_plan_id,
        city_id: cityId,
        phone_number: phone_number,
        preloads: getAdditionalPreloads(ticketTypes),
        reference_id: referenceId,
        reference_type: params?.referenceType,
        ...((startDate || endDate) && {
            created_at: { min: startDate, max: endDate }
        }),
        source_of_registration: params?.source,
        service_type: params?.serviceType,
        company_id: params?.companyId,
        city_ids: params?.cityIds,
        state_ids: params?.stateIds,
        assigned_to: params?.ticketAssignedTo,
        lorry_type: params?.lorryTypeIds || (Number.isInteger(params?.lorryTypeId) ? [params?.lorryTypeId] : undefined),
        description: params?.description,
        disposition_id: params?.dispositionId,
        ticket_types: ticketTypesList?.length ? ticketTypes : undefined,
        status_ids: params?.status_ids?.length ? params?.status_ids : undefined
    };
};

export const useGetTicketsQuery = (
    params: GetTicketsParam,
    options: ClientHookInfiniteQueryOptions<GetTicketsResponse, unknown, TicketData>
) => {
    const { POST } = useVahakHttpClient();
    function getTickets({ pageParam = 1 }) {
        const payload = {
            page_number: pageParam,
            ...mapPayload(params)
        };

        return POST<GetTicketsParam, GetTicketsResponse>(API.GET_TICKETS, payload);
    }

    return useVahakInfiniteQuery<GetTicketsResponse, unknown>(
        [QueryNames.ticketSystem.getTickets, params],
        getTickets,
        {
            getNextPageParam: (lastPage, allPages) => {
                if (lastPage.data.page_number < lastPage.data.total_pages) return lastPage.data.page_number + 1;
                return undefined;
            },
            ...options,
            onSuccess: (data) => {
                let aggregatedData: TicketData[] = [];
                data.pages.forEach((data) => {
                    aggregatedData = [...aggregatedData, ...data.data.tickets_list];
                });
                options.onSuccess(aggregatedData);
            }
        }
    );
};

export const useGetTicketsMutationQuery = () => {
    const { POST } = useVahakHttpClient();

    const method = (params: GetTicketsParam) => {
        const payload = mapPayload(params);
        return POST<typeof payload, GetTicketsResponse>(API.GET_TICKETS, payload);
    };

    return useVahakMutationQuery<GetTicketsResponse, ApiErrorResponseType, GetTicketsParam>(
        [QueryNames.ticketSystem.getTickets],
        method
    );
};
